import { createSharedComposable } from '@vueuse/core';
import { computed, ref } from 'vue';
import { api } from '/app/plugins/services';
import { useRoute } from 'vue-router';
import { useAuth } from './auth';
import { useAppState } from './app-state';
import { inject } from 'vue';
import { usePlatform as usePlatformBySlugQuery } from '/app/queries/platform';
import { useData } from './data';
import { ignoredSubdomains, resolvePlatform, resolvePlatformSlug, DEFAULT_PLATFORM, platforms, userPlatformRules, RESERVED_SLUGS, isSubplatformHost, isTestHost, fromPlatformResponse } from '/app/lib/platform';
import { useRouteParams } from '@vueuse/router';

const hostname = typeof window !== 'undefined' ? location.hostname : '';
const [, domain, subdomain] = hostname.split('.').reverse();

const isTestMode = isTestHost(subdomain);

export const usePlatformUtils = createSharedComposable(() => {
	const route = useRoute();
	const isUserPlatform = computed(() => {
		return userPlatformRules.some((rule) => {
			return typeof rule === 'function' ? rule(route) : rule.test(route.name);
		});
	});

	const isProfilePage = computed(() => /dashboard\.|account\./.test(route.name));

	const isSubplatform = computed(() => isSubplatformHost(subdomain));

	const isTestMode = computed(() => isTestHost(subdomain));

	const isUserPlatformView = computed(() => {
		return isUserPlatform.value && !isSubplatform.value;
	});

	return {
		isUserPlatform,
		isProfilePage,
		isSubplatform,
		isTestMode,
		isUserPlatformView,
	}
});

export const useContextAccount = createSharedComposable(() => {
	const { auth } = useAuth();
	const { isProfilePage } = usePlatformUtils();
	const { contextUser } = useAppState();

	return computed(() => {
		if (isProfilePage.value && auth.authenticated) {
			return auth.current;
		}

		return contextUser.value;
	});
})

export const useUserPlatform = createSharedComposable(() => {
	const account = useContextAccount();
	const { isProfilePage, isUserPlatform } = usePlatformUtils();
	const { auth } = useAuth();

	const routePlatformSlug = useRouteParams('platformSlug');

	const userSlug = computed(() => {
		return account.value && account.value.user.screen_name
	});

	const platformSlug = computed(() => {
		const slug = routePlatformSlug.value;

		if (!isProfilePage.value && isUserPlatform.value && slug && !RESERVED_SLUGS.includes(slug)) {
			return slug;
		}

		return userSlug.value;
	});

	const { data: platform } = usePlatformBySlugQuery(platformSlug)

	const title = computed(() => {
		return account.value ? `${account.value.user.first_name} ${account.value.user.last_name}` : null;
	});

	const description = computed(() => {
		return account.value && account.value.user.bio;
	});

	const slogan = computed(() => {
		return account.value && account.value.user.slogan;
	});

	const isLoggedUser = computed(() => {
		return account.value && account.value.user.id === auth.user.id;
	});

	const isViewingSelf = computed(() => {
		return isProfilePage.value && isLoggedUser.value;
	});

	const isViewingSelfPlatform = computed(() => {
		return platform.value?.owner === auth.user.id;
	});

	const isSuspended = computed(() => {
		return !!platform.value?.suspended;
	});

	const platformId = computed(() => {
		return platform.value?.id;
	});

	return { title, description, slogan, platform, account, isLoggedUser, isViewingSelf, isViewingSelfPlatform, platformId, isSuspended };
});

export const usePlatform = createSharedComposable(() => {
	const platform = ref(
		resolvePlatform(subdomain) ||
		resolvePlatform(domain) ||
		resolvePlatform(DEFAULT_PLATFORM)
	);
	const subPlatform = ref(resolvePlatform(subdomain));
	const { isViewingSelf, platform: userPlatform } = useUserPlatform();
	const { isUserPlatform, isProfilePage, isSubplatform, isUserPlatformView, contextPlatform } = usePlatformUtils();

	const currentPlatform = computed(() => {
		if (isSubplatform.value) {
			return subPlatform.value;
		}

		if (isUserPlatform.value) {
			return userPlatform.value;
		}

		return platform.value;
	});

	const isBignerve = computed(() => {
		return currentPlatform.value?.name === DEFAULT_PLATFORM;
	});

	const title = computed(() => {
		return currentPlatform.value?.title;
	});

	const description = computed(() => {
		if (!currentPlatform.value) {
			return null;
		}

		return isSubplatform.value
			? currentPlatform.value.description
			: currentPlatform.value.slogan;
	});

	const slogan = computed(() => {
		return currentPlatform.value?.slogan;
	});

	// fill main platform
	if (domain) {
		api.platform.find(resolvePlatformSlug(domain)).then(({ data: response } = {}) => {
			// keep the static fallback when the API returns no platform
			if (response?.data) {
				platform.value = fromPlatformResponse(response);
			}
		}).catch(() => {});
	}

	// fill subPlatform
	if (subdomain) {
		api.platform.find(resolvePlatformSlug(subdomain)).then(({ data: response } = {}) => {
			if (response?.data) {
				subPlatform.value = fromPlatformResponse(response);
			}
		}).catch(() => {});
	}

	return {
		title,
		description,
		slogan,
		platform,
		currentPlatform,
		subPlatform,
		contextPlatform: currentPlatform,
		rootPlatform: platform,
		isUserPlatform,
		isProfilePage,
		isViewingSelf,
		isBignerve,
		isSubplatform,
		isUserPlatformView,
	};
});

export const usePlatformStaff = () => {
	return inject('platformStaff');	
};

export const usePlatformSlug = createSharedComposable(() => {
	return useRouteParams('platformSlug', subdomain || domain, {
		transform: (v) => v === 'platform' ? subdomain || domain : v,
	});
});

export const useMyPlatform = createSharedComposable(() => {
	const { auth } = useAuth();

	const { data: platform } = usePlatformBySlugQuery(() => auth.user.screen_name)

	return { platform };
})

export {
	platforms,
	ignoredSubdomains,
	resolvePlatform,
	resolvePlatformSlug,
	userPlatformRules,
	DEFAULT_PLATFORM,
	isSubplatformHost,
	isTestHost,

    domain,
    subdomain,
	isTestMode,
}
